import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import PublicNav from '../components/PublicNav';
import { 
  Trophy, Users, Target, Award, HelpCircle, 
  Zap, BarChart3, ArrowRight, Flame, Calendar
} from 'lucide-react';

export default function RulesPage() {
  const scoringRules = [
    { event: 'Tribe or Team Wins Challenge', points: '+3', positive: true },
    { event: 'Individual Immunity Win', points: '+5', positive: true },
    { event: 'Finds an Idol or Advantage', points: '+2', positive: true },
    { event: 'Plays Idol or Advantage Successfully', points: '+5', positive: true },
    { event: 'Correct Shot in the Dark', points: '+10', positive: true },
    { event: 'Vote Cast for Eliminated Player', points: '+2', positive: true },
    { event: 'Won Fire Making Challenge', points: '+7', positive: true },
    { event: 'Final Tribal Council Appearance', points: '+15', positive: true },
    { event: 'Wins Sole Survivor', points: '+30', positive: true },
    { event: 'Receives Votes but Survives', points: '-2', positive: false },
    { event: 'Voted Out at Tribal Council', points: '-5', positive: false },
    { event: 'Quits or Medically Evacuated', points: '-10', positive: false },
  ];

  return (
    <div className="min-h-screen pb-24 md:pb-0" style={{ backgroundColor: '#F5F0E6' }}>
      <Helmet>
        <title>How Survivor Fantasy League Scoring Works | Survivor Fantasy League</title>
        <meta name="description" content="Learn how points are earned and lost in Survivor Fantasy League. Full scoring breakdown, draft rules, and admin guide for league commissioners." />
        <link rel="canonical" href="https://survivorfantasy.live/rules" />
        <meta property="og:title" content="How Survivor Fantasy League Scoring Works" />
        <meta property="og:description" content="Learn how points are earned and lost in Survivor Fantasy League. Full scoring breakdown, draft rules, and admin guide." />
        <meta property="og:url" content="https://survivorfantasy.live/rules" />
      </Helmet>

      <PublicNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#FF7F50' }} />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#4682B4' }} />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            The Rules. All of Them.
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto" style={{ color: '#F5F0E6' }}>
            How points are earned, how drafts work, and what the League Admin is actually doing every week.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          
          {/* Section 1: The Basics */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FF7F50' }}>
                <Target className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                The Basics
              </h2>
            </div>
            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                Survivor Fantasy League is a season-long fantasy competition tied to the CBS show Survivor. Each player drafts real castaways and earns points based on what those castaways do in the game — winning challenges, finding idols, surviving tribal councils, and ultimately winning the season.
              </p>
              <p>
                The player with the most cumulative points at the end of the season wins the league.
              </p>
              <p>
                There are no live odds, no waivers, and no trades once the draft is complete. The castaways you draft on day one are your castaways for the whole season. Choose wisely.
              </p>
            </div>
          </section>

          {/* Section 2: The Draft */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#4682B4' }}>
                <Users className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                The Draft
              </h2>
            </div>
            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                Every league runs a snake draft before the season starts (or before the league joins mid-season). The League Admin sets the draft order and chooses the format:
              </p>
              <ul className="space-y-3 my-6">
                <li className="flex items-start gap-3">
                  <span className="font-bold text-[#FF7F50]">Snake draft</span> — the classic format. Pick order reverses each round, so the person who picks first in round one picks last in round two. This is the fairest format for competitive leagues.
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-bold text-[#FF7F50]">Random order</span> — the draft order is randomized each round. More chaotic, less predictable.
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-bold text-[#FF7F50]">Custom order</span> — the League Admin manually sets the order. Useful when your group has strong opinions about fairness.
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-bold text-[#FF7F50]">Auto draft (ranked choice)</span> — each player ranks the full contestant list ahead of time. The system drafts automatically in snake order based on each player's rankings. This is the best option for remote leagues when everyone can't be online at the same time.
                </li>
              </ul>
              <p>
                Draft picks are locked once submitted. The League Admin can enable a pick timer (30–120 seconds) to keep live drafts moving.
              </p>
              <p>
                In leagues with more teams than contestants, double drafting can be enabled so players can draft the same castaway more than once. This is typically only relevant for very large leagues.
              </p>
            </div>
          </section>

          {/* Section 3: Scoring */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1E3A4C' }}>
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                Scoring
              </h2>
            </div>
            <div className="prose prose-lg max-w-none mb-8" style={{ color: '#4682B4' }}>
              <p>
                Points are awarded by the League Admin after each episode. The default scoring table is below, but every league can customize point values — the admin sets this up before the season starts.
              </p>
            </div>
            
            {/* Scoring Table */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-4 mb-8" style={{ borderColor: '#FF7F50' }}>
              <div className="px-6 py-4" style={{ backgroundColor: '#FF7F50' }}>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Trophy className="w-6 h-6" />
                  Default Point Values
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                {scoringRules.map((rule, i) => (
                  <div key={i} className="flex items-center justify-between px-6 py-4 hover:bg-[#F5F0E6] transition-colors">
                    <span className="font-medium" style={{ color: '#1E3A4C' }}>{rule.event}</span>
                    <span className={`text-xl font-bold ${rule.positive ? 'text-emerald-600' : 'text-red-500'}`}>
                      {rule.points}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                Points are not automatic. After each episode airs, the League Admin logs the events that happened — who won immunity, who got voted out, who played an idol — and finalizes scoring. Players are notified when the episode is scored.
              </p>
              <p>
                If the admin makes a mistake, they can correct an event and trigger a recalculation. The leaderboard updates automatically.
              </p>
            </div>
          </section>

          {/* Section 4: Bonus Questions */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FF7F50' }}>
                <HelpCircle className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                Bonus Questions
              </h2>
            </div>
            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                Each episode, the League Admin can post bonus questions — predictions players answer before the episode airs. These add another layer of strategy and keep everyone engaged even if their drafted castaways are having a quiet week.
              </p>
              <p className="font-medium" style={{ color: '#1E3A4C' }}>Examples of bonus questions:</p>
              <ul>
                <li>Who will win individual immunity this episode?</li>
                <li>Will a hidden immunity idol be played at tribal council?</li>
                <li>Which tribe will win the reward challenge?</li>
              </ul>
              <p>
                Bonus questions are scored manually by the admin after the episode. Points from bonus questions stack on top of draft points in the overall leaderboard.
              </p>
              <p>
                The League Admin can write bonus questions from scratch or use AI-generated suggestions as a starting point. Either way, nothing gets posted to players until the admin approves it.
              </p>
            </div>
          </section>

          {/* Section 5: The Leaderboard */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#4682B4' }}>
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                The Leaderboard
              </h2>
            </div>
            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                The leaderboard shows cumulative points for every player in the league across all scored episodes. You can see the full rankings, individual player breakdowns, and point trends over time.
              </p>
              <p>
                If you're in last place through the merge, don't quit — finale night is worth a lot. A castaway appearing at Final Tribal Council earns +15 points, and the winner earns +30. A single well-drafted finalist can swing the leaderboard dramatically in the last episode.
              </p>
            </div>
          </section>

          {/* Section 6: Multi-Season Leagues */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1E3A4C' }}>
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                Multi-Season Leagues
              </h2>
            </div>
            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                Leagues persist between seasons. When a new season starts, the League Admin creates a new season under the same league, and players draft again. Season results are stored separately, so you can look back at historical standings. This keeps your friend group together year over year without anyone needing to set up a new league.
              </p>
            </div>
          </section>

          {/* CTA */}
          <div className="text-center py-8 px-6 rounded-2xl" style={{ backgroundColor: '#1E3A4C' }}>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ready to start your league?
            </h3>
            <p className="mb-6" style={{ color: '#F5F0E6' }}>
              Create a free account and set up your first draft.
            </p>
            <Link to="/login?tab=register">
              <Button size="lg" className="text-white px-8" style={{ backgroundColor: '#FF7F50' }}>
                Sign Up Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo-new.png" alt="Survivor Fantasy League" className="w-10 h-10" />
              <div>
                <span className="text-white font-bold text-lg">Survivor Fantasy League</span>
                <p className="text-xs" style={{ color: '#4682B4' }}>The Ultimate Survivor Experience</p>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm" style={{ color: '#F5F0E6' }}>
              <Link to="/" className="hover:text-[#FF7F50] transition-colors">Home</Link>
              <Link to="/how-it-works" className="hover:text-[#FF7F50] transition-colors">How It Works</Link>
              <Link to="/rules" className="hover:text-[#FF7F50] transition-colors">Rules</Link>
              <Link to="/faq" className="hover:text-[#FF7F50] transition-colors">FAQ</Link>
              <Link to="/about" className="hover:text-[#FF7F50] transition-colors">About</Link>
              <Link to="/blog" className="hover:text-[#FF7F50] transition-colors">Blog</Link>
              <Link to="/privacy" className="hover:text-[#FF7F50] transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-[#FF7F50] transition-colors">Terms</Link>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-sm" style={{ borderColor: '#4682B4', color: '#4682B4' }}>
            <p>© {new Date().getFullYear()} Survivor Fantasy League. Not affiliated with CBS or Survivor.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
